import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BasicCli from '../../main/components/basicCli/BasicCli';
import GeneralTable from '../../main/components/grid/GeneralTable';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import {
  IDailyInventoryCloseListItem,
  IDailyInventoryCloseListResponse,
} from '../../main/interfaces/IDailyInventoryClose';
import IGridItems from '../../main/interfaces/IGridItems';
import IPagination from '../../main/interfaces/IPagination';
import useGetUser from '../../main/hooks/useGetUser';
import { useAppTheme } from '../../main/theme/AppThemeContext';
import DailyInventoryClose from './DailyInventoryClose';
import './DailyInventoryCloseGrid.css';

const DIC_GRID_ENTITY = 24;
const DEFAULT_ITEMS_PER_PAGE = [10, 20, 50, 100];

const DEFAULT_GRID_ITEMS: IGridItems[] = [
  { entity: 'ReportDate', label: 'ReportDate', isShown: true },
  { entity: 'TotalRevenue', label: 'TotalRevenue', isShown: true },
  { entity: 'UserCount', label: 'UserCount', isShown: true },
  { entity: 'CreatedByName', label: 'CreatedByName', isShown: true },
  { entity: 'Created', label: 'Created', isShown: true },
];

const normalizeGridItems = (items?: any[]): IGridItems[] => {
  const source = items?.length ? items : DEFAULT_GRID_ITEMS;

  return source.reduce<IGridItems[]>((acc, item) => {
    const entity = String(item.entity ?? item.Entity ?? '').trim();
    if (!entity) return acc;

    acc.push({
      entity,
      label: String(item.label ?? item.Label ?? entity.replace(/\./g, '')),
      isShown: item.isShown ?? item.IsShown ?? true,
      sort: item.sort ?? item.Sort ?? null,
      filter: item.filter ?? item.Filter ?? null,
    });

    return acc;
  }, []);
};

const normalizeReportRow = (item: IDailyInventoryCloseListItem) => ({
  ...item,
  reportDate: String(item.reportDate ?? '').slice(0, 10),
});

const DailyInventoryCloseGrid: React.FC = () => {
  const { i18n } = useTranslation();
  const user = useGetUser();
  const { theme } = useAppTheme();
  const currencySymbol = user?.companyCurrencySymbol ?? '';

  const [reports, setReports] = useState<IDailyInventoryCloseListItem[]>([]);
  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IDailyInventoryCloseListItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [activeReportId, setActiveReportId] = useState<number | null>(null);

  const formatCurrency = useCallback(
    (value: number) => {
      const formatted = new Intl.NumberFormat(i18n.language || 'sq-AL', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Number(value || 0));
      return currencySymbol ? `${formatted} ${currencySymbol}` : formatted;
    },
    [currencySymbol, i18n.language],
  );

  const fetchTableData = useCallback(
    async (pag?: IPagination, gridOverride?: IGridItems[]) => {
      const effectivePagination = pag ?? pagination;
      const effectiveGrid = normalizeGridItems(gridOverride ?? gridItems);

      try {
        const res = await axiosInstance.post<IDailyInventoryCloseListResponse>(
          ApiConstants.dailyInventoryClose.listCt,
          {
            gridItems: effectiveGrid,
            pagination: effectivePagination,
          },
        );

        const data = res.data as any;
        const items = (data?.items ?? data?.Items ?? []).map(normalizeReportRow);
        const p: Partial<IPagination> = data?.pagination ?? data?.Pagination ?? {};
        const cols = normalizeGridItems(data?.gridItems ?? data?.GridItems ?? effectiveGrid);

        setReports(items);
        setGridItems(cols);
        setPagination((prev) => ({
          ...prev,
          count: p.count ?? prev.count,
          page: p.page ?? prev.page,
          itemPerPage: p.itemPerPage ?? prev.itemPerPage,
          itemsPerPage: p.itemsPerPage?.length ? p.itemsPerPage : prev.itemsPerPage,
        }));
      } catch (e) {
        console.error(e);
        toast.error(
          i18n.t('dailyInventoryCloseGrid.messages.loadError', {
            defaultValue: 'Failed to load reports.',
          }),
        );
      }
    },
    [gridItems, pagination, i18n],
  );

  useEffect(() => {
    fetchTableData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = (row: IDailyInventoryCloseListItem) => {
    setActiveReportId(row.id);
    setCloseDialogOpen(true);
  };

  const openNew = () => {
    setActiveReportId(null);
    setCloseDialogOpen(true);
  };

  const closeReportDialog = () => {
    setCloseDialogOpen(false);
    setActiveReportId(null);
  };

  const confirmDelete = (row: IDailyInventoryCloseListItem) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      await axiosInstance.delete(ApiConstants.dailyInventoryClose.delete(deleteTarget.id));
      toast.success(
        i18n.t('dailyInventoryCloseGrid.messages.deleted', {
          defaultValue: 'Report #{{id}} deleted.',
          id: deleteTarget.id,
        }),
      );
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await fetchTableData();
    } catch (e: any) {
      console.error(e);
      toast.error(
        i18n.t('dailyInventoryCloseGrid.messages.deleteError', {
          defaultValue: 'Failed to delete report.',
        }),
      );
    } finally {
      setDeleting(false);
    }
  };

  const enrichedData = useMemo(
    () =>
      reports.map((item) => ({
        ...item,
        _formattedRevenue: formatCurrency(item.totalRevenue),
        _userCount: item.userCount,
        _createdByName: item.createdByName ?? '',
      })),
    [reports, formatCurrency],
  );

  return (
    <BasicCli>
      <div className="bcPageContent">
        <div className="dicGridContainer">
          <div className="bcPageHeader">
            <div>
              <h1 className="bcPageTitle">
                {i18n.t('dailyInventoryCloseGrid.title', { defaultValue: 'Daily Inventory Close' })}
              </h1>
              <Typography className="dicGridSubtitle">
                {i18n.t('dailyInventoryCloseGrid.subtitle', {
                  defaultValue: 'View, edit and manage end-of-day inventory close reports.',
                })}
              </Typography>
            </div>
            <div className="bcPageHeaderActions">
              <button className="ui-control ui-btn ui-btn--primary" onClick={openNew} type="button">
              {i18n.t('dailyInventoryCloseGrid.actions.newReport', {
                defaultValue: 'New Report',
              })}
              </button>
            </div>
          </div>

          <div className="dicGridTable">
            <GeneralTable
              data={enrichedData}
              columns={gridItems}
              gridEntity={DIC_GRID_ENTITY}
              title=""
              hasRp
              selectedItem={openEdit}
              onEdit={openEdit}
              onDelete={confirmDelete}
              pagination={pagination}
              selectedItemPagination={(val) => {
                setPagination(val);
                fetchTableData(val);
              }}
              onFilterClose={(nextGridItems) => {
                setGridItems(nextGridItems);
                fetchTableData(undefined, nextGridItems);
              }}
            />
          </div>
        </div>
      </div>

      <DailyInventoryClose
        open={closeDialogOpen}
        reportId={activeReportId}
        onClose={closeReportDialog}
        onSaved={() => fetchTableData()}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: `dicGridDeleteDialog bcTheme-${theme}` }}
      >
        <DialogTitle className="dicGridDeleteTitle">
          {i18n.t('dailyInventoryCloseGrid.deleteDialog.title', {
            defaultValue: 'Delete report',
          })}
        </DialogTitle>
        <DialogContent className="dicGridDeleteBody">
          <Typography>
            {i18n.t('dailyInventoryCloseGrid.deleteDialog.message', {
              defaultValue:
                'Are you sure you want to delete report #{{id}} from {{date}}? This action cannot be undone.',
              id: deleteTarget?.id ?? '',
              date: deleteTarget?.reportDate ?? '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions className="dicGridDeleteActions">
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            {i18n.t('actions.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={<DeleteOutline />}
          >
            {deleting
              ? i18n.t('generic.deleting', { defaultValue: 'Deleting...' })
              : i18n.t('actions.delete', { defaultValue: 'Delete' })}
          </Button>
        </DialogActions>
      </Dialog>
    </BasicCli>
  );
};

export default DailyInventoryCloseGrid;
